import { useId, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ScribeApiError } from '@scribe/core/client';
import { isMock } from '../lib/mock';
import { useExternalDirectory } from '../lib/graphDirectory';
import {
  useCreateShare,
  useDeleteShare,
  useMe,
  useProvisionAndShare,
  useShares,
  useUsersByIds,
} from '../lib/queries';
import { Avatar, IconButton, Modal } from './ui';
import { UserPicker } from './UserPicker';
import type {
  PermissionLevel,
  ProvisionUserRequest,
  ResourceType,
  Share,
  UserSummary,
} from '@scribe/core/types';

const PERM_OPTIONS: PermissionLevel[] = ['viewer', 'commenter', 'editor', 'owner'];

// SHARE-3 (docs/specs/share-3.md): the free-text Entra oid input used to be submitted straight
// through as `principal_id`, but the shares ACL matches against the platform `users.id`, not
// the oid — an oid is uuid-shaped so it passed validation, but the resulting share never granted
// anyone access (dead share). The directory picker fixes this by construction: it always
// submits a selected `UserSummary.id`. The "team" principal option is hidden until a teams
// management story exists (existing team share rows still render + revoke below).
//
// SHARE-7 (docs/specs/share-7.md §5.4): the picker now optionally merges in a browser-side
// Microsoft Graph people search, so a candidate may be a platform user OR an Entra org member
// who has never signed in. `selected` becomes a `ShareCandidate` union; the external branch
// provisions a platform `users` row (`POST /users/provision`) before the normal `createShare`.
type ShareCandidate = { kind: 'platform'; user: UserSummary } | { kind: 'external'; person: ProvisionUserRequest };

export function ShareDialog({
  resourceType,
  resourceId,
  onClose,
}: {
  resourceType: ResourceType;
  resourceId: string;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<ShareCandidate | null>(null);
  const [perm, setPerm] = useState<PermissionLevel>('viewer');
  const [done, setDone] = useState<string | null>(null);
  const [provisionError, setProvisionError] = useState<string | null>(null);
  const titleId = useId();

  const me = useMe();
  const shares = useShares(resourceType, resourceId);
  const createShare = useCreateShare(resourceType, resourceId);
  const provisionAndShare = useProvisionAndShare(resourceType, resourceId);
  const deleteShare = useDeleteShare(resourceType, resourceId);
  const { source: externalSource, requestConsent } = useExternalDirectory();

  function submit() {
    if (!selected) return;
    setDone(null);
    setProvisionError(null);

    if (selected.kind === 'platform') {
      const person = selected.user;
      createShare.mutate(
        { principalType: 'user', principalId: person.id, perm },
        {
          onSuccess: () => {
            setDone(
              isMock()
                ? `Mock mode — recorded the share locally, no backend was called (user ${person.display_name} as ${perm}).`
                : `Shared with user ${person.display_name} as ${perm}.`,
            );
            setSelected(null);
          },
        },
      );
      return;
    }

    const person = selected.person;
    provisionAndShare.mutate(
      { person, perm },
      {
        onSuccess: () => {
          setDone(
            isMock()
              ? `Mock mode — recorded the share locally, no backend was called (user ${person.display_name} as ${perm}).`
              : `Shared with user ${person.display_name} as ${perm}.`,
          );
          setSelected(null);
        },
        onError: (err) => {
          if (err instanceof ScribeApiError && err.status === 409) {
            setProvisionError('That email is already linked to a different account.');
          }
        },
      },
    );
  }

  const isPending = createShare.isPending || provisionAndShare.isPending;
  const helperCopy =
    externalSource === undefined
      ? 'Share with people in your organization. Anyone you pick must have signed in to Scribe at least once.'
      : "Share with anyone in your organization. People who haven't signed in to Scribe yet will see it when they first sign in.";

  return (
    <Modal onClose={onClose} labelledBy={titleId}>
      <h2 id={titleId}>Share this {resourceType}</h2>
      <p className="muted">{helperCopy}</p>
      {done && (
        <p className="muted" style={{ color: 'var(--ink)' }}>
          {done}
        </p>
      )}
      {createShare.isError && <p className="error">{(createShare.error as Error).message}</p>}
      {provisionError && <p className="error">{provisionError}</p>}
      {!provisionError && provisionAndShare.isError && (
        <p className="error">{(provisionAndShare.error as Error).message}</p>
      )}
      <div className="field">
        <span className="label">Share with</span>
        {selected ? (
          <span className="user-picker-chip">
            <Avatar name={selected.kind === 'platform' ? selected.user.display_name : selected.person.display_name} size="sm" />
            <span className="user-picker-chip-name">
              {selected.kind === 'platform' ? selected.user.display_name : selected.person.display_name}
            </span>
            <IconButton
              label="Clear selected person"
              className="user-picker-chip-clear"
              onClick={() => setSelected(null)}
            >
              <XMarkIcon width={14} height={14} aria-hidden="true" />
            </IconButton>
          </span>
        ) : (
          <UserPicker
            onSelect={(user) => setSelected({ kind: 'platform', user })}
            onSelectExternal={(person) => setSelected({ kind: 'external', person })}
            externalSource={externalSource}
            onRequestConsent={requestConsent}
            excludeIds={me.data ? [me.data.id] : []}
            excludeOids={me.data ? [me.data.entra_oid] : []}
          />
        )}
      </div>
      <div className="field">
        <span className="label">Permission</span>
        <select value={perm} onChange={(e) => setPerm(e.target.value as PermissionLevel)}>
          {PERM_OPTIONS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>
      <div className="row">
        <button disabled={!selected || isPending} onClick={submit}>
          {isPending ? 'Sharing…' : 'Share'}
        </button>
        <button className="ghost" onClick={onClose}>
          Close
        </button>
      </div>

      <div className="row-secondary">
        <h3 className="label" style={{ marginBottom: 8 }}>
          Currently shared with
        </h3>
        <ShareList shares={shares.data ?? []} isLoading={shares.isLoading} deleteShare={deleteShare} />
      </div>
    </Modal>
  );
}

function ShareList({
  shares,
  isLoading,
  deleteShare,
}: {
  shares: Share[];
  isLoading: boolean;
  deleteShare: ReturnType<typeof useDeleteShare>;
}) {
  const [confirmKey, setConfirmKey] = useState<string | null>(null);

  // SHARE-2/#7: batch-resolve every user-principal id on the list in one request (team
  // principals are left as raw ids — the team UI is hidden in SHARE-3, but legacy rows may
  // still exist).
  const userIds = shares.filter((s) => s.principal_type === 'user').map((s) => s.principal_id);
  const { byId } = useUsersByIds(userIds);

  if (isLoading) return <p className="muted">Loading shares…</p>;
  if (shares.length === 0) {
    return <p className="muted">Not shared with anyone yet.</p>;
  }

  return (
    <ul className="share-list">
      {shares.map((s) => {
        const key = `${s.principal_type}:${s.principal_id}`;
        const confirming = confirmKey === key;
        const user = s.principal_type === 'user' ? byId.get(s.principal_id) : undefined;
        return (
          <li key={s.id}>
            <span style={{ flex: 1 }}>
              <strong>{s.principal_type}</strong>{' '}
              {s.principal_type === 'user' ? (
                <>
                  {user?.display_name ?? '…'}
                  {user && <span className="muted"> {user.email}</span>}
                </>
              ) : (
                s.principal_id
              )}
            </span>
            <span className="tag">{s.perm}</span>
            {confirming ? (
              <>
                <button
                  className="danger sm"
                  disabled={deleteShare.isPending}
                  onClick={() =>
                    deleteShare.mutate(
                      { principalType: s.principal_type, principalId: s.principal_id },
                      { onSettled: () => setConfirmKey(null) },
                    )
                  }
                >
                  Confirm revoke
                </button>
                <button className="ghost sm" onClick={() => setConfirmKey(null)}>
                  Cancel
                </button>
              </>
            ) : (
              <button className="ghost sm" onClick={() => setConfirmKey(key)}>
                Revoke
              </button>
            )}
          </li>
        );
      })}
      {deleteShare.isError && <p className="error">{(deleteShare.error as Error).message}</p>}
    </ul>
  );
}
